import React, { useEffect, useMemo, useState } from 'react';
import { obtenerVisitasPendientes } from '../db/indexedDB';
import { API_BASE, formatApiError, getStoredUser, getToken, isAuthError, notifySessionExpired } from '../services/api';
import { sincronizarVisitasPendientes } from '../utils/offlineSync';

const normalizarVisitaFinalizada = (visitaFinalizada) => {
  if (!visitaFinalizada) return null;
  return {
    id: visitaFinalizada.visita_id,
    pdf_url: visitaFinalizada.pdf_url,
    cliente: visitaFinalizada.cliente,
    tipo_actividad: visitaFinalizada.visitaData?.tipo_actividad,
    obs_visita: visitaFinalizada.visitaData?.obs_visita,
    nom_client: visitaFinalizada.visitaData?.nom_client,
    fotos: Array.from({ length: visitaFinalizada.totalFotos || 0 }),
    fech_termino: visitaFinalizada.fecha,
    pdf_disponible: Boolean(visitaFinalizada.pdf_url),
  };
};

const resolverUrlPdf = (pdfUrl) => {
  if (!pdfUrl) return '';
  if (/^https?:\/\//i.test(pdfUrl)) return pdfUrl;

  const ruta = pdfUrl.startsWith('/') ? pdfUrl : `/${pdfUrl}`;
  if (ruta.startsWith('/api/')) {
    return `${API_BASE.replace(/\/api\/?$/, '')}${ruta}`;
  }

  return `${API_BASE}${ruta}`;
};

const ResumenVisita = ({ visitaFinalizada }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [sincronizando, setSincronizando] = useState(false);
  const [pendientesOffline, setPendientesOffline] = useState(0);
  const [resumenTecnico, setResumenTecnico] = useState(null);
  const [resumenLoading, setResumenLoading] = useState(true);
  const [resumenError, setResumenError] = useState('');
  const [visitaSeleccionada, setVisitaSeleccionada] = useState(() => normalizarVisitaFinalizada(visitaFinalizada));
  const [preguntarDescarga, setPreguntarDescarga] = useState(Boolean(visitaFinalizada?.pdf_url));
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState('');

  const visitas = resumenTecnico?.visitas || [];
  const visitaActiva = visitaSeleccionada || visitas[0] || null;
  const cliente = visitaActiva?.cliente || {};
  const avance = resumenTecnico?.avance || 0;
  const puedeDescargarPdf = ['admin', 'superadmin'].includes(getStoredUser()?.rol);

  useEffect(() => {
    if (visitaFinalizada) {
      setVisitaSeleccionada(normalizarVisitaFinalizada(visitaFinalizada));
      setPreguntarDescarga(Boolean(visitaFinalizada.pdf_url));
    }
  }, [visitaFinalizada]);

  useEffect(() => {
    const cargarResumen = async () => {
      setResumenLoading(true);
      setResumenError('');
      try {
        const res = await fetch(`${API_BASE}/visitas/mis-resumen`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          if (isAuthError(res, data)) notifySessionExpired();
          throw new Error(formatApiError(res, data, 'No se pudo cargar el resumen del técnico.'));
        }
        setResumenTecnico(data);
        if (!visitaFinalizada && data.visitas?.length) {
          setVisitaSeleccionada(data.visitas[0]);
        }
      } catch (error) {
        setResumenError(error.message || 'No se pudo cargar el resumen del técnico.');
      } finally {
        setResumenLoading(false);
      }
    };

    cargarResumen();
  }, [visitaFinalizada]);

  const sincronizarDatosPendientes = async () => {
    setSincronizando(true);
    try {
      const resultado = await sincronizarVisitasPendientes();
      const restantes = await obtenerVisitasPendientes();
      setPendientesOffline(restantes.length);
      if (resultado.synced > 0) {
        setResumenError('');
      }
    } catch (error) {
      console.error('Error sincronizando', error);
    } finally {
      setSincronizando(false);
    }
  };

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      sincronizarDatosPendientes();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (navigator.onLine) {
      sincronizarDatosPendientes();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleDescargarPdf = async (visita = visitaActiva) => {
    if (!puedeDescargarPdf || !visita?.pdf_url) return;
    setDescargandoPdf(true);
    setErrorDescarga('');
    try {
      const res = await fetch(resolverUrlPdf(visita.pdf_url), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (isAuthError(res, data)) notifySessionExpired();
        throw new Error(formatApiError(res, data, 'No se pudo descargar el PDF.'));
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const nombreCliente = visita.cliente?.cod_cliente || 'cliente';
      const gestion = visita.cliente?.num_gestion || 'gestion';
      const link = document.createElement('a');
      link.href = url;
      link.download = `${nombreCliente}_${gestion}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setPreguntarDescarga(false);
    } catch (error) {
      setErrorDescarga(error.message || 'No se pudo descargar el PDF.');
    } finally {
      setDescargandoPdf(false);
    }
  };

  const fechaVisita = useMemo(() => {
    if (!visitaActiva?.fech_termino && !visitaActiva?.fecha_ini) return '-';
    return new Date(visitaActiva.fech_termino || visitaActiva.fecha_ini).toLocaleString('es-CL');
  }, [visitaActiva]);

  return (
    <div className="gv-card visit-summary-card">
      <div className={`connection-pill ${isOnline ? 'online' : 'offline'}`}>
        {isOnline ? 'ONLINE' : 'MODO OFFLINE'}
      </div>

      <p className="gv-section-title">Resumen operativo</p>
      <h2>Avance y respaldos de visitas</h2>
      <p>Consulta tu progreso y revisa el resumen de tus visitas registradas.</p>

      {resumenLoading && <p className="import-empty">Cargando avance del técnico...</p>}
      {resumenError && <div className="gv-alert gv-alert--error">{resumenError}</div>}

      {resumenTecnico && (
        <>
          <div className="progress-kpi-grid">
            <div>
              <span>Total asignado</span>
              <strong>{resumenTecnico.total}</strong>
            </div>
            <div>
              <span>OT finalizadas</span>
              <strong>{resumenTecnico.finalizadas}</strong>
            </div>
            <div>
              <span>Visitas realizadas</span>
              <strong>{resumenTecnico.intentos ?? resumenTecnico.finalizadas}</strong>
            </div>
            <div>
              <span>Pendientes</span>
              <strong>{resumenTecnico.pendientes}</strong>
            </div>
          </div>
          <div className="visit-progress">
            <div>
              <span>Avance</span>
              <strong>{avance}%</strong>
            </div>
            <div className="visit-progress__bar">
              <span style={{ width: `${Math.min(avance, 100)}%` }} />
            </div>
          </div>
        </>
      )}

      {puedeDescargarPdf && preguntarDescarga && visitaActiva?.pdf_url && (
        <div className="gv-alert gv-alert--success visit-summary-download">
          <span>PDF generado. ¿Deseas descargarlo ahora?</span>
          <button type="button" className="gv-btn gv-btn--primary gv-btn--sm" onClick={() => handleDescargarPdf()} disabled={descargandoPdf}>
            {descargandoPdf ? 'Descargando...' : 'Descargar PDF'}
          </button>
          <button type="button" className="gv-btn gv-btn--ghost gv-btn--sm" onClick={() => setPreguntarDescarga(false)}>
            Después
          </button>
        </div>
      )}
      {errorDescarga && <div className="gv-alert gv-alert--error">{errorDescarga}</div>}

      {visitaActiva ? (
        <>
          <div className="visit-summary-grid">
            <div>
              <span>N° Gestión</span>
              <strong>{cliente.num_gestion || '-'}</strong>
            </div>
            <div>
              <span>Código cliente</span>
              <strong>{cliente.cod_cliente || '-'}</strong>
            </div>
            <div>
              <span>Cliente</span>
              <strong>{cliente.r_social || '-'}</strong>
            </div>
            <div>
              <span>Comuna</span>
              <strong>{cliente.comuna || '-'}</strong>
            </div>
            <div>
              <span>Resultado</span>
              <strong>{visitaActiva.resultado_label || visitaActiva.resultado_visita || 'Finalizada'}</strong>
            </div>
            <div>
              <span>Actividad</span>
              <strong>{visitaActiva.tipo_actividad || 'No informada'}</strong>
            </div>
            <div>
              <span>Fotos adjuntas</span>
              <strong>{visitaActiva.fotos?.length || 0}</strong>
            </div>
            <div>
              <span>Fecha cierre</span>
              <strong>{fechaVisita}</strong>
            </div>
            <div>
              <span>Atendido por</span>
              <strong>{visitaActiva.nom_client || 'No informado'}</strong>
            </div>
          </div>

          <div className="visit-summary-notes">
            <span>Observaciones</span>
            <p>{visitaActiva.motivo_no_cierre || visitaActiva.obs_visita || 'Sin observaciones registradas.'}</p>
          </div>

          {puedeDescargarPdf && (
            <div className="visit-summary-actions">
              <button type="button" className="gv-btn gv-btn--primary" onClick={() => handleDescargarPdf()} disabled={!visitaActiva.pdf_url || descargandoPdf}>
                {descargandoPdf ? 'Descargando...' : 'Descargar PDF'}
              </button>
            </div>
          )}
        </>
      ) : !resumenLoading && (
        <p className="import-empty">Aún no tienes visitas registradas para mostrar en el resumen.</p>
      )}

      {visitas.length > 0 && (
        <div className="visit-history">
          <p className="gv-section-title">Historial de visitas</p>
          {visitas.map((visita) => (
            <button
              key={visita.id}
              type="button"
              className={`visit-history-row${visitaActiva?.id === visita.id ? ' active' : ''}`}
              onClick={() => {
                setVisitaSeleccionada(visita);
                setPreguntarDescarga(false);
              }}
            >
              <span>{visita.cliente?.cod_cliente} | Gestión {visita.cliente?.num_gestion}</span>
              <strong>{visita.cliente?.r_social || 'Cliente sin razón social'}</strong>
              <em>{visita.resultado_label || visita.resultado_visita || 'Visita'} · {visita.fotos?.length || 0} fotos</em>
            </button>
          ))}
        </div>
      )}

      {sincronizando && <div className="gv-alert gv-alert--warning">Sincronizando datos almacenados...</div>}
      {pendientesOffline > 0 && <p className="import-empty">Visitas offline pendientes detectadas: {pendientesOffline}</p>}
    </div>
  );
};

export default ResumenVisita;
